<template>
  <v-container>
    <transition name="fade">
      <v-alert v-if="api.isError" dense border="left" type="warning">
        {{ api.error }}
      </v-alert>
    </transition>
    <div class="text-h5 pa-3">{{ $t("view.PageMainPlayerMission.title") }}</div>

    <div v-if="!playerData && !isLoading" class="text-center mt-8">
      <v-card flat class="mx-auto" style="max-width: 400px;">
        <v-card-text class="pa-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4">
            mdi-account-search
          </v-icon>
          <div class="text-h6 mb-2 text--secondary">
            {{ $t("string.player_not_found") }}
          </div>
          <div class="text-body-2 text--secondary mb-4">
            <div class="text-h5 pa-3">{{ $t("view.PageMainPlayerMathPerformance.player_not_found_mgs") }}</div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div v-if="!isLoading && playerData" style="max-width: 800px" class="mx-auto">
      <!-- Ongoing Missions Section -->
      <div v-if="ongoingMissions.length > 0" class="mb-6">
        <div class="text-h6 mb-3 d-flex align-center">
          <v-icon color="orange" class="mr-2">mdi-play-circle</v-icon>
          {{ $t("string.missions_ongoing") }}
        </div>
        <v-card
          v-for="(item, i) in ongoingMissions"
          :key="'ongoing-' + i"
          class="mb-3"
          flat
          outlined
        >
          <!-- Mobile Layout -->
          <div class="d-block d-sm-none pa-3">
            <v-row align="center" no-gutters>
              <v-col cols="2">
                <v-avatar size="32" color="orange">
                  <span class="white--text font-weight-bold">{{
                    i + 1
                  }}</span>
                </v-avatar>
              </v-col>
              <v-col cols="10" class="pl-3">
                <div class="font-weight-medium text-body-1">
                  {{ item.questionName || item.questionKey }}
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-3" align="center" no-gutters>
               <v-col cols="4" class="text-center">
                  <div class="text-caption text--secondary mb-1">{{ $t("model.prop.grade") }}</div>
                  <v-img
                    contain
                    max-width="35"
                    class="mx-auto"
                    :src="gradeShields[item.mathRecord && item.mathRecord.grade ? item.mathRecord.grade : 'X']"
                  ></v-img>
                </v-col>

                <v-col cols="4" class="text-center">
                  <div class="text-caption text--secondary mb-1"> {{ $t("model.prop.stars") }}</div>
                  <v-rating
                    :value="item.mathRecord.topStar"
                    background-color="amber"
                    color="amber"
                    length="3"
                    readonly
                    dense
                    size="18"
                  ></v-rating>
                </v-col>

              <v-col cols="4" class="text-center">
                <v-btn
                  color="primary"
                  small
                  @click="playMission(item)"
                >
                  <v-icon small class="mr-1">mdi-play</v-icon>
                  {{ $t("action.play") }}
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Desktop/Tablet Layout -->
          <div class="d-none d-sm-block pa-3">
            <v-row align="center" no-gutters>
              <!-- Topic Number and Name -->
              <v-col cols="6" md="6">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="orange" class="mr-3">
                    <span class="white--text font-weight-bold">{{
                      i + 1
                    }}</span>
                  </v-avatar>
                  <div class="font-weight-medium text-body-1">
                    {{ item.questionName || item.questionKey }}
                  </div>
                </div>
              </v-col>

                <!-- Grade Shield -->
                <v-col cols="2" md="2" class="text-center">
                  <v-img
                    contain
                    max-width="40"
                    class="mx-auto"
                    :src="gradeShields[item.mathRecord && item.mathRecord.grade ? item.mathRecord.grade : 'X']"
                  ></v-img>
                </v-col>

                <!-- Star Rating -->
                <v-col cols="2" md="2" class="text-center">
                  <v-rating
                    :value="item.mathRecord.topStar"
                    background-color="amber"
                    color="amber"
                    length="3"
                    readonly
                    dense
                    size="20"
                  ></v-rating>
                </v-col>

              <!-- Play Button -->
              <v-col cols="2" md="2" class="text-right">
                <v-btn
                  color="primary"
                  @click="playMission(item)"
                >
                  <v-icon class="mr-1">mdi-play</v-icon>
                  {{ $t("action.play") }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>

      <!-- Completed Missions Section -->
      <div v-if="completedMissions.length > 0">
        <div class="text-h6 mb-3 d-flex align-center">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          {{ $t("string.missions_completed") }}
        </div>
        <v-card
          v-for="(item, i) in completedMissions"
          :key="'completed-' + i"
          class="mb-3"
          flat
          outlined
        >
          <!-- Mobile Layout -->
          <div class="d-block d-sm-none pa-3">
            <v-row align="center" no-gutters>
              <v-col cols="2">
                <v-avatar size="32" color="success">
                  <span class="white--text font-weight-bold">{{
                    i+1
                  }}</span>
                </v-avatar>
              </v-col>
              <v-col cols="10" class="pl-3">
                <div class="font-weight-medium text-body-1">
                  {{ item.questionName || item.questionKey }}
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-3" align="center" no-gutters>
              <v-col cols="4" class="text-center">
                <div class="text-caption text--secondary mb-1">{{ $t("model.prop.grade") }}</div>
                <v-img
                  contain
                  max-width="35"
                  class="mx-auto"
                  :src="gradeShields[item.mathRecord && item.mathRecord.grade ? item.mathRecord.grade : 'X']"
                ></v-img>
              </v-col>

              <v-col cols="4" class="text-center">
                <div class="text-caption text--secondary mb-1">{{ $t("model.prop.stars") }}</div>
                <v-rating
                  :value="item.mathRecord ? item.mathRecord.topStar : 0"
                  background-color="amber"
                  color="amber"
                  length="3"
                  readonly
                  dense
                  size="18"
                ></v-rating>
              </v-col>

              <v-col cols="4" class="text-center">
                <div class="text-caption text--secondary mb-1">
                  {{ $t("model.prop.lastUpdated") }}
                </div>
                <div class="text-body-2">
                  <span v-if="item.mathRecord.timestampUpdated">
                    {{ $moment(item.mathRecord.timestampUpdated).format("DD/MM/YY") }}
                  </span>
                  <span v-else class="text--disabled">-</span>
                </div>
                <div
                  class="text-caption text--secondary"
                  v-if="item.mathRecord.timestampUpdated"
                >
                  {{ $moment(item.mathRecord.timestampUpdated).format("HH:mm") }}
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Desktop/Tablet Layout -->
          <div class="d-none d-sm-block pa-3">
            <v-row align="center" no-gutters>
              <!-- Topic Number and Name -->
              <v-col cols="6" md="6">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="success" class="mr-3">
                    <span class="white--text font-weight-bold">{{
                      i+1
                    }}</span>
                  </v-avatar>
                  <div class="font-weight-medium text-body-1">
                    {{ item.questionName || item.questionKey }}
                  </div>
                </div>
              </v-col>

              <!-- Grade Shield -->
              <v-col cols="2" md="2" class="text-center">
                <v-img
                  contain
                  max-width="40"
                  class="mx-auto"
                  :src="gradeShields[item.mathRecord && item.mathRecord.grade ? item.mathRecord.grade : 'X']"
                ></v-img>
              </v-col>

              <!-- Star Rating -->
              <v-col cols="2" md="2" class="text-center">
                <v-rating
                  :value="item.mathRecord ? item.mathRecord.topStar : 0"
                  background-color="amber"
                  color="amber"
                  length="3"
                  readonly
                  dense
                  size="20"
                ></v-rating>
              </v-col>

              <!-- Last Updated -->
              <v-col cols="2" md="2" class="text-right">
                <div class="text-caption text--secondary">{{ $t("model.prop.lastUpdated") }}</div>
                <div class="text-body-2">
                  <span v-if="item.mathRecord.timestampUpdated">
                    {{ $moment(item.mathRecord.timestampUpdated).format("DD MMM YY") }}
                  </span>
                  <span v-else class="text--disabled">-</span>
                </div>
                <div
                  class="text-caption text--secondary"
                  v-if="item.mathRecord.timestampUpdated"
                >
                  {{ $moment(item.mathRecord.timestampUpdated).format("HH:mm") }}
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>

      <!-- No Missions Message -->
      <div v-if="ongoingMissions.length === 0 && completedMissions.length === 0 && !isLoading" class="text-center mt-8">
        <v-card flat class="mx-auto" style="max-width: 400px;">
          <v-card-text class="pa-8">
            <v-icon size="64" color="grey lighten-1" class="mb-4">
              mdi-clipboard-list
            </v-icon>
            <div class="text-h6 mb-2 text--secondary">
              {{ $t("string.missions_not_found") }}
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <div class="text-center mt-5" v-if="isLoading">
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>
  </v-container>
</template>

<script>
export default {
  components: {},
  data: () => ({
    //BOC:[breadcrumbs]
    breadcrumbs: [],
    //EOC
    isLoading: false,
    //BOC:[api]
    api: {
      isLoading: false,
      isError: false,
      error: null,
      url: null,
    },

    apiQuestion: {
      isLoading: false,
      isError: false,
      error: null,
      url: null,
    },
    //EOC
    mochiId: null,
    playerData: null,
    yearRecord: [],
    questionsData: [],
    gradeShields: {
      A: require('@/assets/math/shields/gradeA.png'),
      B: require('@/assets/math/shields/gradeB.png'),
      C: require('@/assets/math/shields/gradeC.png'),
      F: require('@/assets/math/shields/gradeF.png'),
      X: require('@/assets/math/shields/gradeX.png') // fallback
    }
  }),
  
  computed: {
    ongoingMissions() {
      return this.yearRecord
        .filter(record => !record.mathRecord.isCompleted)
        .map(record => {
          const questionData = this.questionsData.find(q => q.key === record.questionKey);
          return {
            ...record,
            questionName: questionData ? questionData.name : null
          };
        });
    },
    
    completedMissions() {
      return this.yearRecord
        .filter(record => record.mathRecord.isCompleted)
        .map(record => {
          const questionData = this.questionsData.find(q => q.key === record.questionKey);
          return {
            ...record,
            questionName: questionData ? questionData.name : null
          };
        });
    }
  },

  created() {
    //BOC:[breadcrumbs]
    this.breadcrumbs.push({
      text: this.$t("route.PageMainHome"),
      to: { name: "PageMainHome" },
      exact: true,
    });
    this.breadcrumbs.push({
      text: this.$t("route.PageMainDiscover"),
      to: { name: "PageMainDiscover" },
      exact: true,
    });
    //EOC
  },
  
  mounted() {
    this.mochiId = this.$route.params.mochiId;
    this.loadPlayerData();
  },
  
  methods: {
    async loadPlayerData() {
      this.isLoading = true;
      
      this.api.method = "post";
      this.api.url =
        this.$api.servers.game +
        "/api/v1/" +
        this.$_getLocale() +
        "/main/mission/visit";

      this.api.callbackReset = () => {
        this.api.isLoading = true;
        this.api.isError = false;
        this.api.error = null;
      };
      
      this.api.callbackError = (e) => {
        this.api.isLoading = false;
        this.api.isError = true;
        this.api.error = e;
        this.isLoading = false;
      };
      
      this.api.callbackSuccess = async (resp) => {
        this.api.isLoading = false;
        this.playerData = resp.Player;
        this.yearRecord = resp.Record;
        await this.fetchQuestionNames();
        this.isLoading = false;
      };
      
      this.api.params = {
        mochiId: this.mochiId,
      };
      
      await this.$api.fetch(this.api);
    },

    async fetchQuestionNames() {
      if (this.yearRecord.length === 0) return;

      // Get unique question keys
      const questionKeys = [...new Set(this.yearRecord.map(record => record.questionKey))];
      
      // Fetch question data for each key
      for (const key of questionKeys) {
        await this.fetchSingleQuestion(key);
      }
    },

    async fetchSingleQuestion(questionKey) {
      this.apiQuestion.method = "post";
      this.apiQuestion.url = `${process.env.VUE_APP_URL_API_QUESTION}/v2/questions`;

      this.apiQuestion.callbackReset = () => {
        this.apiQuestion.isLoading = true;
        this.apiQuestion.isError = false;
        this.apiQuestion.error = null;
      };
      
      this.apiQuestion.callbackError = (e) => {
        this.apiQuestion.isLoading = false;
        this.apiQuestion.isError = true;
        this.apiQuestion.error = e;
        console.error('Error fetching question:', questionKey, e);
      };
      
      this.apiQuestion.callbackSuccess = (resp) => {
        this.apiQuestion.isLoading = false;
        if (resp && resp.length > 0) {
          this.questionsData.push(resp[0]);
        }
      };
      
      this.apiQuestion.params = {
        key: questionKey,
        lang: this.$_getLocale(),
        count: 1,
        showOption: true
      };
      
      await this.$api.fetch(this.apiQuestion);
    },

    getQuestionNumber(questionKey) {
      // Extract number from question key like "s010_c010_q030" -> "30"
      const match = questionKey.match(/q(\d+)$/);
      return match ? parseInt(match[1]) : '?';
    },

    playMission(mission) {
      // Navigate to external play URL
      const playUrl = `${process.env.VUE_APP_URL_WEB_GAME}/${this.$_getLocale()}/math/mastery/${mission.questionKey}/NOGAME`;
      window.open(playUrl, '_blank');
    },

    showError(error) {
      this.api.isError = true;
      this.api.error = error;
    },
  },
};
</script>